import AddIcon from '@mui/icons-material/Add';
import BackupIcon from '@mui/icons-material/Backup';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import RemoveIcon from '@mui/icons-material/Remove';
import SaveIcon from '@mui/icons-material/Save';
import TocIcon from '@mui/icons-material/Toc';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/GridLegacy';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';

import ApiRequest from '../../api/request';
import { AlertContext, Confirm } from '../../components';
import {
  FormInputChange,
  FormInputType,
  FormInputTypeEnum,
  FormValidatedRef,
} from '../../components/Form/Form.types';
import FormLoader from '../../components/Form/FormLoader';
import FormValidated from '../../components/Form/FormValidated';
import { CookieValues, Model } from '../../types';
import DataModifier from '../../utils/DataModifier';
import { Container, DEFAULT_FORM_INPUTS, FORM_INPUT_TYPE_OPTIONS } from './ModelManager.constants';
import SchemaManager from './SchemaManager';

// TODO: Make schema fields read from backend so we can centralize changes to this page
function ModelManager() {
  const [cookies] = useCookies<'token', CookieValues>(['token']);

  const { showAlert } = useContext(AlertContext);

  const modelFormRef = useRef<FormValidatedRef>(null);
  const schemaFormRef = useRef<FormValidatedRef>(null);

  /**
   * This is a hacky way to access the form inputs in a useCallback without needing to re-evaluate it
   * @todo make this not require refs
   */
  const modelFormInputsRef = useRef<FormInputType[]>([]);
  const schemaFormInputsRef = useRef<FormInputType[]>([]);
  const selectedSchemaItemRef = useRef<any>(null);
  const modelRef = useRef<{
    name: string;
    description: string;
    schemaFields: any[];
  } | null>(null);

  const [isAddingModel, setIsAddingModel] = useState<boolean>(false);
  const [isAdminTableSectionExpanded, setIsAdminTableSectionExpanded] = useState<boolean>(false);
  const [isPermissionSectionExpanded, setIsPermissionSectionExpanded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSchemaManagerActive, setIsSchemaManagerActive] = useState<boolean>(false);
  const [isSchemaListExpanded, setIsSchemaListExpanded] = useState<boolean>(true);
  const [model, setModel] = useState<{
    name: string;
    description: string;
    schemaFields: any[];
  } | null>(null);
  const [models, setModels] = useState<Model[]>([]);
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [schemaFormInputs, setSchemaFormInputs] = useState<FormInputType[]>(DEFAULT_FORM_INPUTS);
  const [selectedSchemaItem, setSelectedSchemaItem] = useState<any>('');
  const [tableProperties, setTableProperties] = useState<object>({});
  const [permissions, setPermissions] = useState<object>({});

  const createModel = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await ApiRequest.sendRequest({
        method: 'post',
        params: {
          params: {},
          name: modelFormInputsRef.current[0].value,
          description: modelFormInputsRef.current[1].value,
        },
        path: 'model/create-model',
        token: cookies.token,
      });
      if (response.data.error !== null) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: response.data.error,
          variant: 'danger',
          dismissText: 'okay',
        });
        return;
      }

      setModels(prevModels => DataModifier.sortedInsert<Model>(response.data.results, prevModels, 'name'));
      setIsAddingModel(false);
    } catch (err: any) {
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
        variant: 'danger',
        dismissText: 'okay',
      });
    } finally {
      setIsLoading(false);
    }
  }, [showAlert, cookies.token,]);

  const clearSchemaField = useCallback(async () => {
    setSelectedSchemaItem('');
    setSchemaFormInputs(DEFAULT_FORM_INPUTS);
  }, []);

  const addSchemaField = useCallback(async (evt: any) => {
    evt.preventDefault();

    setIsLoading(true);

    const params: any = {
      id: undefined,
      name: '',
      type: '',
      required: false,
      minLength: -1,
      maxLength: -1,
      lowercase: false,
      trim: false,
      unique: false,
      reference: '',
      autoPopulate: false,
      keyInReference: '',
      values: '',
      modelName: modelRef.current?.name,
    };

    if (selectedSchemaItemRef.current !== '') {
      params.id = selectedSchemaItemRef.current._id;
    }

    for (let i = 0; i < schemaFormInputsRef.current.length; i++) {
      // Skip buttons as they have no label
      if (schemaFormInputsRef.current[i].label !== '') {
        params[schemaFormInputsRef.current[i].id] = schemaFormInputsRef.current[i].value;
      }
    }
    console.log(params);

    try {
      const response = await ApiRequest.sendRequest({
        method: 'post',
        params,
        path: 'model/upsert-schema-field',
        token: cookies.token,
      });
      if (response.data.error !== null) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: response.data.error,
          variant: 'danger',
          dismissText: 'okay',
        });
        return;
      }

      console.log(response.data.results);

      // Put schema field on model and put that model back into list
      setModels(prevModels => {
        const newModels = [...prevModels];
        for (let i = 0; i < prevModels.length; i++) {
          if (prevModels[i].name === modelRef.current?.name) {
            // Adding new schema field
            if (selectedSchemaItemRef.current === '') {
              newModels[i].schemaFields.push(response.data.results);
            } else {
              // Updating existing schema field
              for (let j = 0; j < prevModels[i].schemaFields.length; j++) {
                // @ts-ignore
                if (selectedSchemaItemRef.current._id === prevModels[i].schemaFields[j]._id) {
                  newModels[i].schemaFields[j] = response.data.results;
                  break;
                }
              }
            }
            break;
          }
        }
        return newModels;
      });

      setIsAddingModel(false);

      // Adding new schema field
      // Clear form after
      if (selectedSchemaItemRef.current === '') {
        setSelectedSchemaItem('');
        setSchemaFormInputs(DEFAULT_FORM_INPUTS);
        schemaFormRef.current?.reset();
      } else {
        // Updating schema field, don't clear form
        setSelectedSchemaItem(response.data.results);
      }
    } catch (err: any) {
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
        variant: 'danger',
        dismissText: 'okay',
      });
    } finally {
      setIsLoading(false);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [showAlert, cookies.token]);

  const [modelFormInputs, setModelFormInputs] = useState<FormInputType[]>([
    {
      label: 'Name',
      id: 'name',
      element: 'input',
      type: FormInputTypeEnum.text,
      validation: { requirement: 'A name is required', algorithm: 'range', min: 1, max: 64 },
      required: 'required',
      placeholder: 'Enter a name',
      value: '',
    },
    {
      label: 'Description',
      id: 'description',
      element: 'input',
      type: FormInputTypeEnum.text,
      validation: {
        requirement: 'A description is required',
        algorithm: 'range',
        min: 1,
        max: 512,
      },
      required: 'required',
      placeholder: 'Enter a description',
      value: '',
    },
    {
      label: '',
      id: 'submitBtn',
      element: 'input',
      type: FormInputTypeEnum.submit,
      value: 'Save',
      onClick: createModel,
      class: 'btn-block  .border .border--white',
    },
  ]);

  const stringFormInputs: FormInputType[] = useMemo(
    () => [
      {
        label: 'Name',
        id: 'name',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: { requirement: 'A name is required', algorithm: 'range', min: 1, max: 64 },
        required: 'required',
        placeholder: 'Enter a name',
        value: '',
      },
      {
        label: 'Type',
        id: 'type',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: FORM_INPUT_TYPE_OPTIONS,
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'string',
      },
      {
        label: 'Required',
        id: 'required',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'true',
      },
      {
        label: 'Tooltip',
        id: 'tooltip',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a tooltip (optional)',
        value: '',
      },
      {
        label: 'Tooltip Question',
        id: 'tooltipQuestion',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip question that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a tooltip question (optional)',
        value: '',
      },
      {
        label: 'Tooltip type',
        id: 'tooltipType',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'On label', value: 'onlabel' },
          { text: 'Side Button', value: 'sidebutton' },
        ],
        validation: { requirement: 'Tooltip type is optional if no tooltip', algorithm: 'select' },
        value: '',
      },
      {
        label: 'Label',
        id: 'label',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a label that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a label (optional)',
        value: '',
      },
      {
        label: 'Min length',
        id: 'minLength',
        element: 'input',
        type: FormInputTypeEnum.number,
        validation: {
          requirement: 'Enter zero if you not required',
          algorithm: 'range',
          min: -9999999999999,
          max: 9999999999999,
        },
        required: 'required',
        placeholder: 'Enter a minimum',
        value: 0,
      },
      {
        label: 'Max length',
        id: 'maxLength',
        element: 'input',
        type: FormInputTypeEnum.number,
        validation: {
          requirement: 'Enter -1 if not required',
          algorithm: 'range',
          min: -9999999999999,
          max: 9999999999999,
        },
        required: 'required',
        placeholder: 'Enter a maximum',
        value: 0,
      },
      {
        label: 'Lowercased',
        id: 'lowercase',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'false',
      },
      {
        label: 'Trim whitespace',
        id: 'trim',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'false',
      },
      {
        label: 'Unique',
        id: 'unique',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'false',
      },
      {
        label: 'Managed Update Form',
        id: 'managedUpdateForm',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: 'Is Array',
        id: 'isArray',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: '',
        id: 'cancelBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Cancel',
        onClick: clearSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
      {
        label: '',
        id: 'submitBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Save',
        onClick: addSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
    ],
    [addSchemaField, clearSchemaField],
  );

  const secureFormInputs: FormInputType[] = useMemo(
    () => [
      {
        label: 'Name',
        id: 'name',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: { requirement: 'A name is required', algorithm: 'range', min: 1, max: 64 },
        required: 'required',
        placeholder: 'Enter a name',
        value: '',
      },
      {
        label: 'Type',
        id: 'type',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: FORM_INPUT_TYPE_OPTIONS,
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'string',
      },
      {
        label: 'Required',
        id: 'required',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'true',
      },
      {
        label: 'Tooltip',
        id: 'tooltip',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip that is 0-256 chars',
          algorithm: 'range',
          min: 0,
          max: 256,
        },
        placeholder: 'Enter a tooltip (optional)',
        value: '',
      },
      {
        label: 'Tooltip Question',
        id: 'tooltipQuestion',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip question that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a tooltip question (optional)',
        value: '',
      },
      {
        label: 'Tooltip type',
        id: 'tooltipType',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'On label', value: 'onlabel' },
          { text: 'Side Button', value: 'sidebutton' },
        ],
        validation: { requirement: 'Tooltip type is optional if no tooltip', algorithm: 'select' },
        value: '',
      },
      {
        label: 'Label',
        id: 'label',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a label that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a label (optional)',
        value: '',
      },
      {
        label: 'Min length',
        id: 'minLength',
        element: 'input',
        type: FormInputTypeEnum.number,
        validation: {
          requirement: 'Enter zero if you not required',
          algorithm: 'range',
          min: -9999999999999,
          max: 9999999999999,
        },
        required: 'required',
        placeholder: 'Enter a minimum',
        value: 0,
      },
      {
        label: 'Max length',
        id: 'maxLength',
        element: 'input',
        type: FormInputTypeEnum.number,
        validation: {
          requirement: 'Enter -1 if not required',
          algorithm: 'range',
          min: -9999999999999,
          max: 9999999999999,
        },
        required: 'required',
        placeholder: 'Enter a maximum',
        value: 0,
      },
      {
        label: 'Lowercased',
        id: 'lowercase',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'false',
      },
      {
        label: 'Trim whitespace',
        id: 'trim',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'false',
      },
      {
        label: 'Unique',
        id: 'unique',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'false',
      },
      {
        label: 'Managed Update Form',
        id: 'managedUpdateForm',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: 'Is Array',
        id: 'isArray',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: '',
        id: 'cancelBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Cancel',
        onClick: clearSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
      {
        label: '',
        id: 'submitBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Save',
        onClick: addSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
    ],
    [addSchemaField, clearSchemaField],
  );

  const referenceFormInputs: FormInputType[] = useMemo(
    () => [
      {
        label: 'Name',
        id: 'name',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: { requirement: 'A name is required', algorithm: 'range', min: 1, max: 64 },
        required: 'required',
        placeholder: 'Enter a name',
        value: '',
      },
      {
        label: 'Type',
        id: 'type',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: FORM_INPUT_TYPE_OPTIONS,
        value: 'reference',
      },
      {
        label: 'Required',
        id: 'required',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'true',
      },
      {
        label: 'Tooltip',
        id: 'tooltip',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip that is 0-256 chars',
          algorithm: 'range',
          min: 0,
          max: 256,
        },
        placeholder: 'Enter a tooltip (optional)',
        value: '',
      },
      {
        label: 'Tooltip Question',
        id: 'tooltipQuestion',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip question that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a tooltip question (optional)',
        value: '',
      },
      {
        label: 'Tooltip type',
        id: 'tooltipType',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'On label', value: 'onlabel' },
          { text: 'Side Button', value: 'sidebutton' },
        ],
        validation: { requirement: 'Tooltip type is optional if no tooltip', algorithm: 'select' },
        value: '',
      },
      {
        label: 'Label',
        id: 'label',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a label that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a label (optional)',
        value: '',
      },
      {
        label: 'Unique',
        id: 'unique',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'false',
      },
      {
        label: 'Reference',
        id: 'reference',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'A reference to another model is required if the type is reference',
          algorithm: 'range',
          min: 1,
          max: 64,
        },
        required: 'required',
        placeholder: 'Enter another models name',
        value: '',
      },
      {
        label: 'Key in Reference',
        id: 'keyInReference',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'A key in the reference model is required if the type is reference',
          algorithm: 'range',
          min: 1,
          max: 64,
        },
        required: 'required',
        placeholder: 'Enter another models field name',
        value: '',
      },
      {
        label: 'Auto populate',
        id: 'autoPopulate',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'true',
      },
      {
        label: 'Managed Update Form',
        id: 'managedUpdateForm',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: 'Is Array',
        id: 'isArray',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: '',
        id: 'cancelBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Cancel',
        onClick: clearSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
      {
        label: '',
        id: 'submitBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Save',
        onClick: addSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
    ],
    [addSchemaField, clearSchemaField],
  );

  const numberFormInputs: FormInputType[] = useMemo(
    () => [
      {
        label: 'Name',
        id: 'name',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: { requirement: 'A name is required', algorithm: 'range', min: 1, max: 64 },
        required: 'required',
        placeholder: 'Enter a name',
        value: '',
      },
      {
        label: 'Type',
        id: 'type',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: FORM_INPUT_TYPE_OPTIONS,
        value: 'number',
      },
      {
        label: 'Required',
        id: 'required',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'true',
      },
      {
        label: 'Tooltip',
        id: 'tooltip',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip that is 0-256 chars',
          algorithm: 'range',
          min: 0,
          max: 256,
        },
        placeholder: 'Enter a tooltip (optional)',
        value: '',
      },
      {
        label: 'Tooltip Question',
        id: 'tooltipQuestion',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip question that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a tooltip question (optional)',
        value: '',
      },
      {
        label: 'Tooltip type',
        id: 'tooltipType',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'On label', value: 'onlabel' },
          { text: 'Side Button', value: 'sidebutton' },
        ],
        validation: { requirement: 'Tooltip type is optional if no tooltip', algorithm: 'select' },
        value: '',
      },
      {
        label: 'Label',
        id: 'label',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a label that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a label (optional)',
        value: '',
      },
      {
        label: 'Min value',
        id: 'minLength',
        element: 'input',
        type: FormInputTypeEnum.number,
        validation: {
          requirement: 'Enter -9999999999999 if you not required',
          algorithm: 'range',
          min: -9999999999999,
          max: 9999999999999,
        },
        required: 'required',
        placeholder: 'Enter a minimum',
        value: '-9999999999999',
      },
      {
        label: 'Max value',
        id: 'maxLength',
        element: 'input',
        type: FormInputTypeEnum.number,
        validation: {
          requirement: 'Enter 9999999999999 if not required',
          algorithm: 'range',
          min: -9999999999999,
          max: 9999999999999,
        },
        required: 'required',
        placeholder: 'Enter a maximum',
        value: '9999999999999',
      },
      {
        label: 'Unique',
        id: 'unique',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'false',
      },
      {
        label: 'Managed Update Form',
        id: 'managedUpdateForm',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: 'Is Array',
        id: 'isArray',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: '',
        id: 'cancelBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Cancel',
        onClick: clearSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
      {
        label: '',
        id: 'submitBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Save',
        onClick: addSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
    ],
    [addSchemaField, clearSchemaField],
  );

  const booleanFormInputs: FormInputType[] = useMemo(
    () => [
      {
        label: 'Name',
        id: 'name',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: { requirement: 'A name is required', algorithm: 'range', min: 1, max: 64 },
        required: 'required',
        placeholder: 'Enter a name',
        value: '',
      },
      {
        label: 'Type',
        id: 'type',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: FORM_INPUT_TYPE_OPTIONS,
        value: 'boolean',
      },
      {
        label: 'Required',
        id: 'required',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'true',
      },
      {
        label: 'Tooltip',
        id: 'tooltip',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip that is 0-256 chars',
          algorithm: 'range',
          min: 0,
          max: 256,
        },
        placeholder: 'Enter a tooltip (optional)',
        value: '',
      },
      {
        label: 'Tooltip Question',
        id: 'tooltipQuestion',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip question that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a tooltip question (optional)',
        value: '',
      },
      {
        label: 'Tooltip type',
        id: 'tooltipType',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'On label', value: 'onlabel' },
          { text: 'Side Button', value: 'sidebutton' },
        ],
        validation: { requirement: 'Tooltip type is optional if no tooltip', algorithm: 'select' },
        value: '',
      },
      {
        label: 'Label',
        id: 'label',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a label that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a label (optional)',
        value: '',
      },
      {
        label: 'Managed Update Form',
        id: 'managedUpdateForm',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: 'Is Array',
        id: 'isArray',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: '',
        id: 'cancelBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Cancel',
        onClick: clearSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
      {
        label: '',
        id: 'submitBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Save',
        onClick: addSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
    ],
    [addSchemaField, clearSchemaField],
  );

  const fileFormInputs: FormInputType[] = useMemo(
    () => [
      {
        label: 'Name',
        id: 'name',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: { requirement: 'A name is required', algorithm: 'range', min: 1, max: 64 },
        required: 'required',
        placeholder: 'Enter a name',
        value: '',
      },
      {
        label: 'Type',
        id: 'type',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: FORM_INPUT_TYPE_OPTIONS,
        value: 'file',
      },
      {
        label: 'Required',
        id: 'required',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'true',
      },
      {
        label: 'Tooltip',
        id: 'tooltip',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip that is 0-256 chars',
          algorithm: 'range',
          min: 0,
          max: 256,
        },
        placeholder: 'Enter a tooltip (optional)',
        value: '',
      },
      {
        label: 'Tooltip Question',
        id: 'tooltipQuestion',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip question that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a tooltip question (optional)',
        value: '',
      },
      {
        label: 'Tooltip type',
        id: 'tooltipType',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'On label', value: 'onlabel' },
          { text: 'Side Button', value: 'sidebutton' },
        ],
        validation: { requirement: 'Tooltip type is optional if no tooltip', algorithm: 'select' },
        value: '',
      },
      {
        label: 'Label',
        id: 'label',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a label that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a label (optional)',
        value: '',
      },
      {
        label: 'Managed Update Form',
        id: 'managedUpdateForm',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: 'Is Array',
        id: 'isArray',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: '',
        id: 'cancelBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Cancel',
        onClick: clearSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
      {
        label: '',
        id: 'submitBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Save',
        onClick: addSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
    ],
    [addSchemaField, clearSchemaField],
  );

  const objectFormInputs: FormInputType[] = useMemo(
    () => [
      {
        label: 'Name',
        id: 'name',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: { requirement: 'A name is required', algorithm: 'range', min: 1, max: 64 },
        required: 'required',
        placeholder: 'Enter a name',
        value: '',
      },
      {
        label: 'Type',
        id: 'type',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: FORM_INPUT_TYPE_OPTIONS,
        value: 'object',
      },
      {
        label: 'Required',
        id: 'required',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'true',
      },
      {
        label: 'Tooltip',
        id: 'tooltip',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip that is 0-256 chars',
          algorithm: 'range',
          min: 0,
          max: 256,
        },
        placeholder: 'Enter a tooltip (optional)',
        value: '',
      },
      {
        label: 'Tooltip Question',
        id: 'tooltipQuestion',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip question that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a tooltip question (optional)',
        value: '',
      },
      {
        label: 'Tooltip type',
        id: 'tooltipType',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'On label', value: 'onlabel' },
          { text: 'Side Button', value: 'sidebutton' },
        ],
        validation: { requirement: 'Tooltip type is optional if no tooltip', algorithm: 'select' },
        value: '',
      },
      {
        label: 'Label',
        id: 'label',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a label that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a label (optional)',
        value: '',
      },
      {
        label: 'Managed Update Form',
        id: 'managedUpdateForm',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: 'Is Array',
        id: 'isArray',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: '',
        id: 'cancelBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Cancel',
        onClick: clearSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
      {
        label: '',
        id: 'submitBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Save',
        onClick: addSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
    ],
    [addSchemaField, clearSchemaField],
  );

  const selectFormInputs: FormInputType[] = useMemo(
    () => [
      {
        label: 'Name',
        id: 'name',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: { requirement: 'A name is required', algorithm: 'range', min: 1, max: 64 },
        required: 'required',
        placeholder: 'Enter a name',
        value: '',
      },
      {
        label: 'Type',
        id: 'type',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: FORM_INPUT_TYPE_OPTIONS,
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'select',
      },
      {
        label: 'Required',
        id: 'required',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'true',
      },
      {
        label: 'Tooltip',
        id: 'tooltip',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip that is 0-256 chars',
          algorithm: 'range',
          min: 0,
          max: 256,
        },
        placeholder: 'Enter a tooltip (optional)',
        value: '',
      },
      {
        label: 'Tooltip Question',
        id: 'tooltipQuestion',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip question that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a tooltip question (optional)',
        value: '',
      },
      {
        label: 'Tooltip type',
        id: 'tooltipType',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'On label', value: 'onlabel' },
          { text: 'Side Button', value: 'sidebutton' },
        ],
        validation: { requirement: 'Tooltip type is optional if no tooltip', algorithm: 'select' },
        value: '',
      },
      {
        label: 'Label',
        id: 'label',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a label that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a label (optional)',
        value: '',
      },
      {
        label: 'Values',
        id: 'values',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: { requirement: 'Options are required', algorithm: 'range', min: 1, max: 256 },
        required: 'required',
        placeholder: 'Comma separated values',
        value: '',
      },
      {
        label: 'Unique',
        id: 'unique',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'false',
      },
      {
        label: 'Managed Update Form',
        id: 'managedUpdateForm',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: 'Is Array',
        id: 'isArray',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: '',
        id: 'cancelBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Cancel',
        onClick: clearSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
      {
        label: '',
        id: 'submitBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Save',
        onClick: addSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
    ],
    [addSchemaField, clearSchemaField],
  );

  const datetimeFormInputs: FormInputType[] = useMemo(
    () => [
      {
        label: 'Name',
        id: 'name',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: { requirement: 'A name is required', algorithm: 'range', min: 1, max: 64 },
        required: 'required',
        placeholder: 'Enter a name',
        value: '',
      },
      {
        label: 'Type',
        id: 'type',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: FORM_INPUT_TYPE_OPTIONS,
        value: 'datetime',
      },
      {
        label: 'Required',
        id: 'required',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'true',
      },
      {
        label: 'Tooltip',
        id: 'tooltip',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip that is 0-256 chars',
          algorithm: 'range',
          min: 0,
          max: 256,
        },
        placeholder: 'Enter a tooltip (optional)',
        value: '',
      },
      {
        label: 'Tooltip Question',
        id: 'tooltipQuestion',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip question that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a tooltip question (optional)',
        value: '',
      },
      {
        label: 'Tooltip type',
        id: 'tooltipType',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'On label', value: 'onlabel' },
          { text: 'Side Button', value: 'sidebutton' },
        ],
        validation: { requirement: 'Tooltip type is optional if no tooltip', algorithm: 'select' },
        value: '',
      },
      {
        label: 'Label',
        id: 'label',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a label that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a label (optional)',
        value: '',
      },
      {
        label: 'Managed Update Form',
        id: 'managedUpdateForm',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: 'Is Array',
        id: 'isArray',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: '',
        id: 'cancelBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Cancel',
        onClick: clearSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
      {
        label: '',
        id: 'submitBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Save',
        onClick: addSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
    ],
    [addSchemaField, clearSchemaField],
  );

  const dateFormInputs: FormInputType[] = useMemo(
    () => [
      {
        label: 'Name',
        id: 'name',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: { requirement: 'A name is required', algorithm: 'range', min: 1, max: 64 },
        required: 'required',
        placeholder: 'Enter a name',
        value: '',
      },
      {
        label: 'Type',
        id: 'type',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: FORM_INPUT_TYPE_OPTIONS,
        value: 'date',
      },
      {
        label: 'Required',
        id: 'required',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ],
        validation: { requirement: 'Please choose a value', algorithm: 'select' },
        value: 'true',
      },
      {
        label: 'Tooltip',
        id: 'tooltip',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip that is 0-256 chars',
          algorithm: 'range',
          min: 0,
          max: 256,
        },
        placeholder: 'Enter a tooltip (optional)',
        value: '',
      },
      {
        label: 'Tooltip Question',
        id: 'tooltipQuestion',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a tooltip question that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a tooltip question (optional)',
        value: '',
      },
      {
        label: 'Tooltip type',
        id: 'tooltipType',
        element: 'select',
        type: FormInputTypeEnum.select,
        options: [
          { text: 'On label', value: 'onlabel' },
          { text: 'Side Button', value: 'sidebutton' },
        ],
        validation: { requirement: 'Tooltip type is optional if no tooltip', algorithm: 'select' },
        value: '',
      },
      {
        label: 'Label',
        id: 'label',
        element: 'input',
        type: FormInputTypeEnum.text,
        validation: {
          requirement: 'Enter a label that is 0-64 chars',
          algorithm: 'range',
          min: 0,
          max: 64,
        },
        placeholder: 'Enter a label (optional)',
        value: '',
      },
      {
        label: 'Managed Update Form',
        id: 'managedUpdateForm',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: 'Is Array',
        id: 'isArray',
        element: 'input',
        type: FormInputTypeEnum.checkbox,
        validation: { algorithm: 'checkbox' },
        required: '',
        checked: false,
        value: 'false',
      },
      {
        label: '',
        id: 'cancelBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Cancel',
        onClick: clearSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
      {
        label: '',
        id: 'submitBtn',
        element: 'input',
        type: FormInputTypeEnum.submit,
        value: 'Save',
        onClick: addSchemaField,
        class: 'btn-block  .border .border--white',
        disabled: true,
      },
    ],
    [addSchemaField, clearSchemaField],
  );

  useEffect(() => {
    loadModels();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  // MARK: - APIs
  const loadModels = async () => {
    setIsLoading(true);

    const params = { params: {} };

    try {
      const response = await ApiRequest.sendRequest({
        method: 'post',
        params,
        path: 'model/query-model-collection',
        token: cookies.token,
      });
      if (response.data.error !== null) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: response.data.error,
          variant: 'danger',
          dismissText: 'okay',
        });
        return;
      }

      setModels(response.data.results);
    } catch (err: any) {
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
        variant: 'danger',
        dismissText: 'okay',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getFormInputsForType = (type: FormInputTypeEnum) => {
    switch (type) {
      case 'string':
        return stringFormInputs;

      case 'select':
        return selectFormInputs;

      case 'secure':
        return secureFormInputs;

      case 'reference':
        return referenceFormInputs;

      case 'number':
        return numberFormInputs;

      case 'boolean':
        return booleanFormInputs;

      case 'file':
        return fileFormInputs;

      case 'object':
        return objectFormInputs;

      case 'datetime':
        return datetimeFormInputs;

      case 'date':
        return dateFormInputs;

      default:
        return [];
    }
  };

  /**
		Schema item selected from list, populate form values
		@param 	{SchemaItem}	item 		Schema item
	*/
  const schemaItemSelected = (item: any) => {
    console.log(`ModelManager.schemaItemSelected(${item.name})`);
    if (item !== selectedSchemaItem) {
      console.log(item);
      // Iterate form inputs and update value for selected model values
      const formInputs = getFormInputsForType(item.type);

      // Populate form input values
      for (let i = 0; i < formInputs.length; i++) {
        if (formInputs[i].type !== 'submit') {
          formInputs[i].value =
            item[formInputs[i].id] === undefined && formInputs[i].type === 'checkbox'
              ? false
              : item[formInputs[i].id];
        }
      }

      /*
				Update form inputs
				Form input uses it's state's value
				and since we're not instantiating a new object
				we need to tell the form to update it's value
			*/
      setSelectedSchemaItem(item);
      setSchemaFormInputs(formInputs);
      setPermissions({});
    } else {
      // Already selected, de-select this schema field
      setSelectedSchemaItem('');
      setSchemaFormInputs(DEFAULT_FORM_INPUTS);
    }

    schemaFormRef.current?.reset();
  };

  const modelItemSelected = (item: Model) => {
    console.log('item', item);
    setModel(item);
    setName(item.name);
    setDescription(item.description);
    setTableProperties(item.tableProperties);
    setPermissions(JSON.stringify(item.permissions, undefined, 2) as any)

    // tableProperties: JSON.stringify(item.tableProperties, null, 4),
    // permissions: JSON.stringify(item.permissions, null, 4)
  };

  const modelFormOnChange = (change: FormInputChange, _: boolean) => {
    const key = change.id.substr(change.id.lastIndexOf('.') + 1);
    setModelFormInputs(prevModelFormInputs => {
      const newFormInputs = [...prevModelFormInputs];
      const formInput = newFormInputs.find(input => {
        if ([input.id].includes(key)) {
          return true;
        }
        return false;
      });

      if (formInput) {
        formInput.value = change.value;
      }

      return newFormInputs;
    });
  };

  const schemaFormOnChange = (change: FormInputChange, _: boolean) => {
    let formInputs = null;

    // Handle adjusting required form inputs based on type of field
    if (change.id === 'type') {
      formInputs = getFormInputsForType(change.value);
      // Save name
      let name = '';
      for (let i = 0; i < schemaFormInputs.length; i++) {
        if (schemaFormInputs[i].id === 'name') {
          name = schemaFormInputs[i].value;
          break;
        }
      }

      // Update name in new form inputs
      for (let i = 0; i < formInputs.length; i++) {
        if (formInputs[i].id === 'name') {
          formInputs[i].value = name;
          break;
        }
      }
    } else {
      // Not changing type, update value in current form inputs
      formInputs = [...schemaFormInputs];
      for (let i = 0; i < formInputs.length; i++) {
        if (formInputs[i].id === change.id) {
          formInputs[i].value = change.value;
          break;
        }
      }
    }

    setSchemaFormInputs(formInputs);
  };

  const findModelIndexByName = (name: string) => {
    for (let i = 0; i < models.length; i++) {
      if (models[i].name === name) {
        return i;
      }
    }
    return -1;
  };

  // MARK: - Top buttons
  // Update core model name/description
  const saveOnClick = async () => {
    setIsLoading(true);

    try {
      // Update model
      const response = await ApiRequest.sendRequest({
        method: 'post',
        params: {
          modelName: model?.name,
          newName: name,
          newDescription: description,
          tableProperties: typeof tableProperties === 'string' ? JSON.parse(tableProperties) : tableProperties,
          permissions: typeof permissions === 'string' ? JSON.parse(permissions) : permissions,
        },
        path: 'model/update-model',
        token: cookies.token,
      });
      if (response.data.error !== null) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: response.data.error,
          variant: 'danger',
          dismissText: 'okay',
        });
        return;
      }
      console.log(response.data);

      // Update models array
      const index = findModelIndexByName(model?.name ?? '');
      setModels(prevModels => {
        const newModels = [...prevModels];
        newModels[index] = response.data.results;
        return newModels;
      });
      setModel(response.data.results);
    } catch (err: any) {
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
        variant: 'danger',
        dismissText: 'okay',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Stop editing model
  const quitOnClick = async () => {
    setModel(null);
    setSchemaFormInputs(DEFAULT_FORM_INPUTS);
  };

  const backupOnClick = async () => {
    setIsLoading(true);
    try {
      const response = await ApiRequest.sendRequest({
        method: 'post',
        params: {},
        path: 'model/backup',
        token: cookies.token,
      });
      console.log(response.data);
      if (response.data.error !== null) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: response.data.error,
          variant: 'danger',
          dismissText: 'okay',
        });
        return;
      }

      showAlert({
        headerText: '',
        detailText: 'Schema and all records backed up',
        variant: 'success',
        dismissText: 'okay',
      });
    } catch (err: any) {
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
        variant: 'danger',
        dismissText: 'okay',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // MARK: - Bottom buttons
  const deleteModelOnClick = async () => {
    const message = `Are you sure you want to delete the model for ${model?.name}`;
    const confirmed = await Confirm(message);
    if (confirmed) {
      setIsLoading(true);

      try {
        const response = await ApiRequest.sendRequest({
          method: 'post',
          params: { name: model?.name },
          path: 'model/delete-model-collection',
          token: cookies.token,
        });
        if (response.data.error !== null) {
          showAlert({
            headerText: 'Uh-oh',
            detailText: response.data.error,
            variant: 'danger',
            dismissText: 'okay',
          });
          return;
        }

        console.log(response.data.results);

        setModels(prevModels => {
          const newModels = [...prevModels];
          for (let i = 0; i < newModels.length; i++) {
            if (newModels[i].name === model?.name) {
              newModels.splice(i, 1);
              break;
            }
          }
          return newModels;
        });

        setIsAddingModel(false);
        setModel(null);
      } catch (err: any) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
          variant: 'danger',
          dismissText: 'okay',
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // MARK: - Schema field buttons
  const deleteSchemaFieldOnClick = async () => {
    console.log('ModelManager.deleteSchemaFieldOnClick()');
    const message = `Are you sure you want to delete the schema field for ${selectedSchemaItem.name}`;
    const confirmed = await Confirm(message);
    if (confirmed) {
      setIsLoading(true);

      try {
        const response = await ApiRequest.sendRequest({
          method: 'post',
          params: {
            modelName: model?.name,
            id: selectedSchemaItem._id,
          },
          path: 'model/delete-schema-field',
          token: cookies.token,
        });
        if (response.data.error !== null) {
          showAlert({
            headerText: 'Uh-oh',
            detailText: response.data.error,
            variant: 'danger',
            dismissText: 'okay',
          });
          return;
        }

        setModel(response.data.results);
      } catch (err: any) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
          variant: 'danger',
          dismissText: 'okay',
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // MARK: - Model form
  const addNewModelOnClick = async () => setIsAddingModel(true);

  const removeNewModelOnClick = async () => {
    setIsAddingModel(false);
  };

  useEffect(() => {
    modelFormInputsRef.current = modelFormInputs;
  }, [modelFormInputs]);

  useEffect(() => {
    schemaFormInputsRef.current = schemaFormInputs;
  }, [schemaFormInputs]);

  useEffect(() => {
    selectedSchemaItemRef.current = selectedSchemaItem;
  }, [selectedSchemaItem]);

  useEffect(() => {
    modelRef.current = model;
  }, [model]);

  const modelList: React.ReactNode[] = [];
  if (models && model === null) {
    models.forEach((model, index) => {
      modelList.push(
        <ListItem
          component="button"
          key={`model-list-item${index}`}
          onClick={() => {
            modelItemSelected(model);
          }}>
          <ListItemText primary={model.name} />
        </ListItem>,
      );
    });
  }

  console.log(permissions);
  return (
    <Container>
      <FormLoader isLoading={isLoading} />

      {isSchemaManagerActive ? (
        <>
          <Button
            color="secondary"
            startIcon={<ExitToAppIcon />}
            onClick={() => setIsSchemaManagerActive(false)}>
            Back to Model Manager
          </Button>
          <SchemaManager />
        </>
      ) : (
        <Grid container spacing={1}>
          <Grid item xs={1} />

          <Grid item xs={2}>
            {model !== null && (
              <>
                <Grid item xs={12} className="main-btn-section">
                  <Button color="primary" startIcon={<SaveIcon />} onClick={saveOnClick}>
                    Save
                  </Button>
                  <Button color="secondary" startIcon={<ExitToAppIcon />} onClick={quitOnClick}>
                    Quit
                  </Button>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    id="outlined-basic"
                    label="Name"
                    variant="outlined"
                    className="schema-name"
                    onChange={e => {
                      setName(e.target.value);
                    }}
                    value={name}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="outlined-basic"
                    label="Description"
                    variant="outlined"
                    className="schema-name"
                    onChange={e => {
                      setDescription(e.target.value);
                    }}
                    value={description}
                  />
                </Grid>

                <Grid item xs={12} className="schema-list">
                  <Button
                    color="primary"
                    startIcon={<UnfoldMoreIcon />}
                    onClick={() => {
                      setIsSchemaListExpanded(!isSchemaListExpanded);
                      setIsAdminTableSectionExpanded(false);
                      setIsPermissionSectionExpanded(false);
                    }}>
                    {isSchemaListExpanded ? 'Hide schema items' : 'Show schema items'}
                  </Button>

                  {model &&
                    isSchemaListExpanded &&
                    model.schemaFields.map((item, index) => (
                      <Grid item xs={12} key={`schema-list-item${index}`}>
                        <Grid item xs={12}>
                          <Chip
                            label={item.name}
                            clickable
                            color="primary"
                            icon={selectedSchemaItem === item ? <DoneIcon /> : undefined}
                            variant="outlined"
                            onClick={() => schemaItemSelected(item)}
                          />
                        </Grid>
                        {selectedSchemaItem === item && (
                          <Grid item xs={12} className="schema-btns">
                            <Grid item xs={12}>
                              <Button color="primary" startIcon={<FileCopyIcon />}>
                                Copy Field
                              </Button>
                              <Button
                                color="primary"
                                startIcon={<DeleteIcon />}
                                onClick={deleteSchemaFieldOnClick}>
                                Delete Field
                              </Button>
                            </Grid>
                          </Grid>
                        )}
                      </Grid>
                    ))}

                  <Grid item xs={12}>
                    <Button
                      className="table-properties-btn"
                      color="primary"
                      startIcon={<TocIcon />}
                      onClick={() => {
                        setIsAddingModel(!isAdminTableSectionExpanded);
                        setIsAdminTableSectionExpanded(!isAdminTableSectionExpanded);
                      }}>
                      {isAdminTableSectionExpanded
                        ? 'Hide Admin Table Properties'
                        : 'Edit Admin Table Properties'}
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      className="table-properties-btn"
                      color="primary"
                      startIcon={<TocIcon />}
                      onClick={() => {
                        setIsPermissionSectionExpanded(!isPermissionSectionExpanded);
                      }}>
                      {isPermissionSectionExpanded
                        ? 'Hide Access Permissions'
                        : 'Edit Access Permissions'}
                    </Button>
                  </Grid>
                </Grid>
                <Grid item xs={12} className="bottom-btn-section">
                  <Button color="primary" startIcon={<DeleteIcon />} onClick={deleteModelOnClick}>
                    Delete Model
                  </Button>
                </Grid>
              </>
            )}

            {model === null && !isAddingModel && (
              <Grid item xs={12} className="model-list">
                <h1>
                  Models <AddIcon onClick={addNewModelOnClick} />
                </h1>
                <Paper>
                  <List component="nav" aria-label="model list">
                    {modelList}
                  </List>
                </Paper>
                <Button color="primary" startIcon={<BackupIcon />} onClick={backupOnClick}>
                  Backup To Cloud
                </Button>
                <Button
                  color="success"
                  startIcon={<FormatListNumberedIcon />}
                  onClick={() => setIsSchemaManagerActive(true)}>
                  Switch to Schema Manager
                </Button>
              </Grid>
            )}

            {model === null && isAddingModel && (
              <Grid item xs={12} className="model-list">
                <h2>
                  New Model <RemoveIcon onClick={removeNewModelOnClick} />
                </h2>
                <Paper>
                  <FormValidated
                    ref={modelFormRef}
                    formInputs={modelFormInputs}
                    formOnChange={modelFormOnChange}
                    dynamicInputs={false}
                    validateOnInit={false}
                    columns={1}
                    description=""
                  />
                </Paper>
              </Grid>
            )}
          </Grid>

          <Grid item xs={1} />

          {model !== null && !isAdminTableSectionExpanded && !isPermissionSectionExpanded && (
            <Grid item xs={7} className="schema-form">
              <FormValidated
                ref={schemaFormRef}
                formInputs={schemaFormInputs}
                formOnChange={schemaFormOnChange}
                dynamicInputs
                validateOnInit={false}
                columns={2}
                description=""
              />
            </Grid>
          )}

          {model !== null && isAdminTableSectionExpanded && !isPermissionSectionExpanded && (
            <Grid item xs={7} className="admin-table-properties">
              <TextField
                id="outlined-basic"
                label="Table Properties"
                variant="outlined"
                multiline
                className="table-properties"
                rows={18}
                onChange={e => {
                  setTableProperties(JSON.parse(e.target.value));
                }}
                value={JSON.stringify(tableProperties, undefined, 2)}
              />
              <Button
                color="primary"
                startIcon={<FormatIndentIncreaseIcon />}
                onClick={() => {
                  console.log('TODO!!!');
                }}>
                Format
              </Button>
            </Grid>
          )}

          {model !== null && !isAdminTableSectionExpanded && isPermissionSectionExpanded && (
            <Grid item xs={7} className="admin-table-properties">
              <TextField
                id="outlined-basic"
                label="Table Properties"
                variant="outlined"
                multiline
                className="table-properties"
                rows={18}
                onChange={e => {
                  setPermissions(e.target.value as any);
                }}
                value={permissions}
              />
              <Button
                color="primary"
                startIcon={<FormatIndentIncreaseIcon />}
                onClick={() => {
                  console.log('TODO!!!');
                }}>
                Format
              </Button>
            </Grid>
          )}

          <Grid item xs={1} />
        </Grid>
      )}
    </Container>
  );
}

export default ModelManager;
